import * as DG from "ui/dialogs"
import { ChangeEvent, useCallback, useEffect, useState } from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import { createMember, updateMember, fetchUnassignedUsers } from "api/users"
import { debounce } from "lodash/fp"
import { queryCache, useMutation, useQuery } from "react-query"

import AutoCompleteInput from "components/inputs/AutoComplete"
import Button from "ui/buttons/Button"
import FormControl from "@material-ui/core/FormControl"
import { MarketTeam } from "../../TeamMembers/types"
import { MultiSelect } from "../../components/MultiSelect"
import Options from "components/inputs/Options"
import { css, cx } from "@emotion/css"
import { fetchAgencies } from "api/agencies"
import { fetchCompanies } from "api/companies"
import { limitAndOffset } from "api/utils"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"
import { MorePopover } from "components/MorePopover"
import { TextInput } from "components/inputs/TextInput"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"

export type Props = {
  open: boolean
  onClose: VoidFunction
  resetMember: VoidFunction
  member?: Partial<MarketTeam>
}

const marketRoles = [
  "AGENCY_ADMINISTRATOR",
  "MANAGING_DIRECTOR",
  "AGENCY_FINANCE",
  "PLANNER",
  "BILLER",
  "BUYER",
]

function optionsMapped<T extends Record<string, any>>(data?: T[]) {
  return (
    data?.map((d) => ({
      value: d.id,
      name: d.name,
    })) || []
  )
}

function ManageMember(props: Props) {
  const { open, onClose, member, resetMember } = props
  const { t } = useI18n()
  const ls = useLocationSearch()
  const [state, setState] =
    useState<Partial<Omit<MarketTeam, "companyIds" | "agencyIds">>>()
  const [multipleIds, setMultipleIds] = useState<{
    companyIds?: { name: string; value: string }[]
    agencyIds?: { name: string; value: string }[]
  }>()
  const [errors, setErrors] = useState<ErrorsMap | undefined>()
  const [memberTerm, setMemberTerm] = useState<string>()
  const [openMulti, setOpenMulti] = useState<boolean>(false)

  const { user } = useAuthenticated()
  const params = ls.map(limitAndOffset).toString()

  // ** fetch unassigned users **
  const memberParams = !!memberTerm
    ? new URLSearchParams([["search", memberTerm]]).toString()
    : ""
  const {
    data: unassignedUsers,
    updatedAt,
    isFetching,
  } = useQuery(["unassigned-users", memberParams], fetchUnassignedUsers)

  // ** fetch companies **
  const { data: companies } = useQuery(
    ["companies", params, user?.agency_id],
    fetchCompanies,
  )

  // ** fetch agencies **
  const { data: agencies } = useQuery(
    ["market-agencies", params],
    fetchAgencies,
  )

  const [create] = useMutation(createMember, {
    onSuccess: () => {
      queryCache.invalidateQueries("market-teams")
      queryCache.invalidateQueries("unassigned-users")
      handleClose()
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const [update] = useMutation(updateMember, {
    onSuccess: () => {
      queryCache.invalidateQueries("market-teams")
      queryCache.invalidateQueries("unassigned-users")
      handleClose()
      resetMember()
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const companiesMapped = optionsMapped(companies?.data)

  const agenciesMapped = optionsMapped(agencies?.data)

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = evt.target
    !!name && setState((s) => ({ ...s, [name]: value }))
    setErrors(undefined)
    setMultipleIds(undefined)
  }

  const handleNameChange = useCallback((memberId: string | null) => {
    setMemberTerm(undefined)
    setErrors(undefined)
    setState((s) => ({
      ...s,
      name: memberId || undefined,
    }))
  }, [])

  const handleSubmit = () => {
    const companiesIds = multipleIds?.companyIds?.map((c) => c.value)
    const agencyIds = multipleIds?.agencyIds?.map((a) => a.value)
    const editable = {
      agencyIds: agencyIds,
      companyIds: companiesIds,
    }
    const rest = {
      id: state?.name,
      role: state?.role,
    }
    const pld = {
      ...rest,
      ...editable,
    }
    !member ? create(pld) : update({ ...editable, userId: member?.id || "" })
  }

  const handleClose = () => {
    setState(undefined)
    setMultipleIds(undefined)
    onClose()
    resetMember()
  }

  const handleToggleOption =
    (entity: string) =>
    (selectedOptions: { name: string; value: string }[]) => {
      setMultipleIds(() => ({ [entity]: selectedOptions }))
      setErrors(undefined)
    }

  const handleSelectAll = (entity: string) => (isSelected: boolean) => {
    const ids = entity === "companyIds" ? companiesMapped : agenciesMapped
    if (isSelected) {
      setMultipleIds(() => ({ [entity]: ids || [] }))
      setErrors(undefined)
    } else {
      setMultipleIds(undefined)
      setErrors(undefined)
    }
  }

  const hasAgencyErrors = Object.keys(errors ?? {})?.[0]?.includes("agencyIds")

  const handleAgencyError = () => {
    let agencyErrorsIds: (string | undefined)[] = []
    Object.keys(errors ?? {})?.forEach((err) => {
      const errIdx = err?.slice(-2, -1)
      const errId = multipleIds?.agencyIds?.find(
        (_, idx) => idx?.toString() === errIdx,
      )?.value
      const agencyErrId = agencies?.data?.find((a) => a?.id === errId)?.name
      agencyErrorsIds.push(agencyErrId)
    })

    const errorsMsg = [...new Set(Object.values(errors ?? {}))].toString()
    return `${t(errorsMsg)}: ${agencyErrorsIds}`
  }

  const rolesOptions = marketRoles.map((mr) => ({
    label: t(`role.list.${mr}`),
    value: mr,
  }))
  const memberOptions = unassignedUsers?.map((m) => m.id || "")

  const getMember = useCallback(
    (id: string) => unassignedUsers?.find((m) => m.id === id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updatedAt],
  )

  const handleMemberInputChange = useCallback((value: string) => {
    setMemberTerm(value)
  }, [])

  const isAgencyRole =
    state?.role === "AGENCY_ADMINISTRATOR" ||
    state?.role === "AGENCY_FINANCE" ||
    state?.role === "MANAGING_DIRECTOR"

  const isBiller = state?.role === "BILLER"

  const minimumRequired = !!state?.name && !!state?.role

  const extraRequired = isAgencyRole
    ? !!multipleIds?.agencyIds?.length
    : isBiller
      ? !!multipleIds?.companyIds?.length
      : true

  const canAdd = minimumRequired && extraRequired

  const multiSelectInputValue = (
    values?: {
      name: string
      value: string
    }[],
  ) =>
    values
      ?.slice(0, 1)
      ?.map((c) => c.name)
      .join(", ")

  useEffect(() => {
    if (!open) {
      setState(undefined)
      setErrors(undefined)
    }
    if (!!member) {
      const mappedCompanyIds = optionsMapped(member?.companies)
      const mappedAgencyIds = optionsMapped(member?.agencies)

      const { companyIds, agencyIds, ...rest } = member
      setState(rest)
      setMultipleIds({
        companyIds: mappedCompanyIds,
        agencyIds: mappedAgencyIds,
      })
    }
  }, [open, member])

  const onOpenMulti = () => setOpenMulti(true)

  const onCloseMulti = () => setOpenMulti(false)

  const id = "create-new-member"

  return (
    <DG.Dialog
      className={styles.dialog}
      open={open || false}
      onClose={handleClose}
      id={id}
      disableEscapeKeyDown
      keepMounted
      BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.3)" } }}
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id}>
          {!member
            ? t("teams.member.create.title")
            : t("teams.member.edit.title")}
        </DG.DialogTitle>
        <DG.DialogClose onClose={handleClose} />
      </DG.DialogHeader>

      <DG.DialogContent className={styles.content}>
        <div className={styles.inputWrapper}>
          <FormControl variant="outlined" className={styles.form}>
            {!member ? (
              <AutoCompleteInput<string>
                id="name"
                name="name"
                label={t("teams.member.input.name")}
                margin="normal"
                size="medium"
                getOptionLabel={(id) => {
                  const member = getMember(id)
                  return member ? `${member.firstname} ${member.lastname}` : ""
                }}
                value={state?.name || ""}
                onChange={handleNameChange}
                options={memberOptions}
                getOptionSelected={(opt, val) => opt === val}
                renderOption={(id) => {
                  const member = getMember(id)
                  return member ? (
                    <span>
                      {member.firstname} {member.lastname}
                    </span>
                  ) : (
                    ""
                  )
                }}
                isLoading={isFetching}
                blurOnChange={true}
                onInputChange={debounce(300, (value: string) =>
                  handleMemberInputChange(value),
                )}
                autoComplete="off"
                error={!!errors && !!errors["name"]}
                errorMessage={!!errors ? errors["name"] : ""}
              />
            ) : (
              <TextInput
                name={"name"}
                value={state?.name || ""}
                label={t("teams.member.input.name")}
                margin="normal"
                size="medium"
                disabled
                className={styles.input}
              />
            )}
            <div>
              <Options
                name="role"
                label={t("teams.member.input.role")}
                value={state?.role}
                options={rolesOptions || []}
                onChange={handleChange}
                disabled={!!member}
                margin="normal"
                size="medium"
                className={styles.input}
                error={!!errors ? errors["role"] : ""}
              />
            </div>
            {isAgencyRole && (
              <div>
                <MultiSelect
                  items={agenciesMapped}
                  open={openMulti}
                  onOpen={onOpenMulti}
                  onClose={onCloseMulti}
                  getOptionLabel={(option: Record<string, string>) =>
                    option?.name
                  }
                  label={t("teams.member.input.agency")}
                  selectedValues={multipleIds?.agencyIds || []}
                  renderOption={(option: Record<string, string>) =>
                    option?.name
                  }
                  selectAllOption={{
                    name: t("filters.all"),
                    value: "select-all",
                  }}
                  selectAllValue="select-all"
                  onToggleOption={handleToggleOption("agencyIds")}
                  onSelectAll={handleSelectAll("agencyIds")}
                  paperClassName={css`
                    width: 70%;
                  `}
                  inputClassName={styles.inputWidth}
                  checkValues={() => true}
                  inputValue={
                    multiSelectInputValue(multipleIds?.agencyIds) || ""
                  }
                  endAdornment={
                    <>
                      <MorePopover
                        data={multipleIds?.agencyIds}
                        label={"agencies"}
                        name="name"
                        slice={1}
                      />
                      <span
                        className={cx(styles.icon, { expanded: openMulti })}
                      >
                        <ExpandMoreIcon />
                      </span>
                    </>
                  }
                  errorMessage={
                    hasAgencyErrors ? handleAgencyError() : undefined
                  }
                  onKeyDown={(e) => {
                    e.preventDefault()
                  }}
                />
              </div>
            )}
            {(state?.role === "BUYER" || state?.role === "BILLER") && (
              <div>
                <MultiSelect
                  items={companiesMapped}
                  open={openMulti}
                  onOpen={onOpenMulti}
                  onClose={onCloseMulti}
                  getOptionLabel={(option: Record<string, string>) =>
                    option?.name
                  }
                  label={t("teams.member.input.company")}
                  selectedValues={multipleIds?.companyIds || []}
                  renderOption={(option: Record<string, string>) =>
                    option?.name
                  }
                  selectAllOption={{
                    name: t("filters.all"),
                    value: "select-all",
                  }}
                  selectAllValue="select-all"
                  onToggleOption={handleToggleOption("companyIds")}
                  onSelectAll={handleSelectAll("companyIds")}
                  paperClassName={css`
                    width: 70%;
                  `}
                  inputClassName={styles.inputWidth}
                  checkValues={() => true}
                  inputValue={
                    multiSelectInputValue(multipleIds?.companyIds) || ""
                  }
                  endAdornment={
                    <>
                      <MorePopover
                        data={multipleIds?.companyIds}
                        label={"companies"}
                        name="name"
                        slice={1}
                      />
                      <span
                        className={cx(styles.icon, { expanded: openMulti })}
                      >
                        <ExpandMoreIcon />
                      </span>
                    </>
                  }
                  onKeyDown={(e) => {
                    e.preventDefault()
                  }}
                />
              </div>
            )}
          </FormControl>
        </div>
      </DG.DialogContent>
      <DG.DialogFooter>
        <DG.DialogActions>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            {t("teams.member.button.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="new_member"
            onClick={handleSubmit}
            disabled={!canAdd}
          >
            {!member
              ? t("teams.member.button.add")
              : t("teams.member.button.save")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

const styles = {
  form: css`
    min-width: 275px;
  `,
  inputWrapper: css`
    margin-top: var(--spacing-xs);
  `,
  input: css`
    min-width: 255px;
  `,
  content: css`
    padding-bottom: var(--spacing-xl);
  `,
  dialog: css`
    max-width: 551px;
    margin: auto;
  `,
  inputWidth: css`
    width: 100%;
    margin-top: var(--spacing-m);
    margin-bottom: var(--spacing-xs);
  `,
  icon: css`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    padding-right: 8px;
    padding-left: 8px;

    color: var(--dark-grey);

    &.expanded {
      transform: translateY(-50%) rotate(180deg);
    }
  `,
}

export default ManageMember
